import { useEffect, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusPill } from '@/components/StatusPill';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { LoadErrorState } from '@/components/ListStates';
import { facebookAudiencesService, MetaCustomAudience } from '@/services/facebook-audiences.service';
import { RefreshCw, Users, Trash2, RotateCw, Info, Sparkles, Trophy, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function syncStatusVariant(s: string | null | undefined): 'success' | 'danger' | 'warning' | 'neutral' {
  if (s === 'success') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'running') return 'warning';
  return 'neutral';
}

const DAYS_OPTIONS = [30, 60, 90, 180] as const;
const RATIO_OPTIONS = [
  { label: '1%', value: 0.01 },
  { label: '2%', value: 0.02 },
  { label: '5%', value: 0.05 },
  { label: '10%', value: 0.10 },
] as const;

const COUNTRY_OPTIONS = [
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'SA', label: 'Saudi Arabia' },
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'PK', label: 'Pakistan' },
  { code: 'IN', label: 'India' },
  { code: 'EG', label: 'Egypt' },
  { code: 'OM', label: 'Oman' },
  { code: 'KW', label: 'Kuwait' },
  { code: 'QA', label: 'Qatar' },
  { code: 'BH', label: 'Bahrain' },
  { code: 'JO', label: 'Jordan' },
];

interface LookalikeModalState {
  sinceDays: number;
  country: string;
  ratio: number;
  name: string;
}

interface GenericLookalikeState {
  seedId: string;
  seedName: string;
  country: string;
  ratio: number;
  name: string;
}

export default function AudiencesList() {
  const [items, setItems] = useState<MetaCustomAudience[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [del, setDel] = useState<MetaCustomAudience | null>(null);
  const [lookalikeOpen, setLookalikeOpen] = useState(false);
  const [building, setBuilding] = useState(false);

  // Generic lookalike (from any existing custom audience)
  const [genericOpen, setGenericOpen] = useState(false);
  const [genericBuilding, setGenericBuilding] = useState(false);
  const [genericState, setGenericState] = useState<GenericLookalikeState>({
    seedId: '',
    seedName: '',
    country: 'AE',
    ratio: 0.01,
    name: '',
  });

  const openGenericFor = (seed?: MetaCustomAudience) => {
    if (seed) {
      setGenericState({
        seedId: seed.metaAudienceId,
        seedName: seed.name,
        country: 'AE',
        ratio: 0.01,
        name: `${seed.name} — Lookalike 1% AE`,
      });
    } else {
      setGenericState({ seedId: '', seedName: '', country: 'AE', ratio: 0.01, name: '' });
    }
    setGenericOpen(true);
  };

  const buildGenericLookalike = async () => {
    if (!genericState.seedId) { toast.error('Pick a seed audience'); return; }
    if (!genericState.name.trim()) { toast.error('Name is required'); return; }
    setGenericBuilding(true);
    try {
      const r = await facebookAudiencesService.createLookalike({
        source_audience_id: genericState.seedId,
        country: genericState.country,
        ratio: genericState.ratio,
        name: genericState.name.trim(),
      });
      toast.success(`Lookalike created · Meta ID ${r.metaAudienceId}. Building takes 6–24h on Meta's side.`);
      setGenericOpen(false);
      await load(true);
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setGenericBuilding(false); }
  };

  // Auto-update the suggested name when seed/country/ratio change (only if
  // the user hasn't manually edited it away from the default shape).
  const suggestedGenericName = (s: GenericLookalikeState) => {
    if (!s.seedName) return '';
    return `${s.seedName} — Lookalike ${(s.ratio * 100).toFixed(0)}% ${s.country}`;
  };

  const defaultLookalikeState = (): LookalikeModalState => ({
    sinceDays: 90,
    country: 'AE',
    ratio: 0.01,
    name: 'Won leads (last 90d) — Lookalike 1% AE',
  });

  const [lookalikeState, setLookalikeState] = useState<LookalikeModalState>(defaultLookalikeState());

  // Auto-update suggested name when params change
  const updateSuggestedName = (state: LookalikeModalState) => {
    const ratioLabel = `${(state.ratio * 100).toFixed(0)}%`;
    return `Won leads (last ${state.sinceDays}d) — Lookalike ${ratioLabel} ${state.country}`;
  };

  const setDays = (days: number) => {
    setLookalikeState((s) => {
      const next = { ...s, sinceDays: days };
      next.name = updateSuggestedName(next);
      return next;
    });
  };

  const setCountry = (country: string) => {
    setLookalikeState((s) => {
      const next = { ...s, country };
      next.name = updateSuggestedName(next);
      return next;
    });
  };

  const setRatio = (ratio: number) => {
    setLookalikeState((s) => {
      const next = { ...s, ratio };
      next.name = updateSuggestedName(next);
      return next;
    });
  };

  const load = async (force = false) => {
    setRefreshing(true);
    setLoadError(false);
    try {
      const r = await facebookAudiencesService.list(force);
      setItems(r.items);
    } catch {
      setLoadError(true);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
  }, []);

  const handleBuildLookalike = async () => {
    setBuilding(true);
    try {
      const result = await facebookAudiencesService.buildLookalikeFromWon({
        sinceDays: lookalikeState.sinceDays,
        country: lookalikeState.country,
        ratio: lookalikeState.ratio,
        name: lookalikeState.name || undefined,
      });
      toast.success(
        `Lookalike built! ${result.source.recipientCount.toLocaleString()} won leads uploaded as source.`,
      );
      setLookalikeOpen(false);
      setLookalikeState(defaultLookalikeState());
      load(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to build lookalike audience';
      toast.error(msg);
    } finally {
      setBuilding(false);
    }
  };

  return (
    <>
      <Topbar
        title="Custom Audiences"
        breadcrumb="Facebook Ads / Audiences"
        description={`${items?.length ?? 0} audience${items?.length === 1 ? '' : 's'} synced to Meta`}
        right={
          <button
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary hover:bg-surface-alt hover:text-text-primary disabled:opacity-50 transition-colors"
            onClick={() => load(true)}
            disabled={refreshing}
            aria-label="Refresh"
          >
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
          </button>
        }
      />

      <div className="px-7 py-7 w-full space-y-4">

        {/* Two side-by-side lookalike CTAs */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Won-leads Lookalike CTA — CRM shortcut */}
          <div className="flex items-start gap-4 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary-soft to-transparent px-5 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-on-primary shadow-soft">
              <Trophy size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-text-primary">Lookalike from Won leads</div>
              <div className="mt-0.5 text-sm text-text-secondary">
                Hash and upload your won-lead phone numbers, then build a "similar to these" audience in one click.
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              iconLeft={<Sparkles size={14} />}
              onClick={() => setLookalikeOpen(true)}
              className="shrink-0"
            >
              Build
            </Button>
          </div>

          {/* Generic lookalike CTA — from any existing custom audience */}
          <div className="flex items-start gap-4 rounded-2xl border border-info/20 bg-gradient-to-r from-info-soft to-transparent px-5 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-info text-white shadow-soft">
              <Wand2 size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-text-primary">Lookalike from any custom audience</div>
              <div className="mt-0.5 text-sm text-text-secondary">
                Pick any existing custom audience as a seed. Or click the wand icon next to any audience below.
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Wand2 size={14} />}
              onClick={() => openGenericFor()}
              disabled={!items || items.filter(a => a.metaSubtype !== 'LOOKALIKE').length === 0}
              className="shrink-0"
            >
              Build
            </Button>
          </div>
        </div>

        {/* How it works banner */}
        <div className="flex items-start gap-3 rounded-xl border border-info/30 bg-info-soft px-4 py-3">
          <Info size={15} className="mt-0.5 shrink-0 text-info" />
          <p className="text-xs text-info">
            <strong>How it works:</strong> Sync your CRM contact groups to Meta as Custom Audiences. Only
            opted-in contacts are included. Email and phone are SHA-256 hashed before upload — Meta matches
            against their database. Synced audiences become available for ad targeting in your Ad Sets.
          </p>
        </div>

        {/* Audiences card */}
        <Card padded={false} className="overflow-hidden">
          <SectionHeader
            title="Audiences"
            icon={<Users size={16} />}
            className="px-5 pt-5 pb-0 mb-0"
          />

          {items === null && loadError ? (
            <LoadErrorState bare what="your audiences" onRetry={() => void load(false)} />
          ) : items === null ? (
            <div className="space-y-2 p-5 mt-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="skeleton h-16 rounded-xl" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-14 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary">
                <Users size={24} />
              </div>
              <div>
                <div className="font-semibold text-text-primary">No Custom Audiences yet</div>
                <div className="mt-1 text-sm text-text-muted">
                  Head to Audience → Groups, click into a group, and use <strong>Sync to Meta</strong> to create one.
                  Or use the "Build a lookalike from your Won leads" button above.
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              {/* Header */}
              <div className="grid grid-cols-[2.5fr_1fr_1fr_1fr_60px] gap-4 border-b border-border px-5 pb-2.5 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
                <span>Audience</span>
                <span>Approx. size</span>
                <span>Delivery</span>
                <span>Last sync</span>
                <span />
              </div>

              {items.map((a) => (
                <div
                  key={a.audienceUid}
                  className="grid grid-cols-[2.5fr_1fr_1fr_1fr_60px] gap-4 items-center border-b border-border px-5 py-4 last:border-b-0 hover:bg-surface-alt transition-colors"
                >
                  <div>
                    <div className="font-semibold text-text-primary">{a.name}</div>
                    <div className="mt-0.5 font-mono text-xs text-text-muted">{a.metaAudienceId}</div>
                    {a.sourceGroupUid && (
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-primary">
                        <RotateCw size={10} />
                        Synced from CRM group
                      </div>
                    )}
                    {(a as MetaCustomAudience & { sourceMode?: string }).sourceMode === 'won_leads' && (
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                        <Trophy size={10} />
                        Built from Won leads
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="tabular-nums font-bold text-text-primary">
                      {a.approximateCount?.toLocaleString() ?? '—'}
                    </div>
                    <div className="text-xs text-text-muted">
                      {a.lastSyncedRecipientCount != null
                        ? `${a.lastSyncedRecipientCount.toLocaleString()} uploaded`
                        : ''}
                    </div>
                  </div>

                  <div className="text-xs text-text-secondary">
                    <div>{a.deliveryStatus ?? '—'}</div>
                    <div className="text-text-muted">{a.operationStatus ?? ''}</div>
                  </div>

                  <div>
                    {a.lastSyncedAt ? (
                      <div>
                        <div className="text-xs text-text-secondary">{relativeTime(a.lastSyncedAt)}</div>
                        <div className="mt-0.5">
                          <StatusPill
                            status={a.lastSyncStatus ?? 'unknown'}
                            variant={syncStatusVariant(a.lastSyncStatus)}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-text-muted">never</span>
                    )}
                  </div>

                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => openGenericFor(a)}
                      disabled={a.metaSubtype === 'LOOKALIKE'}
                      title={a.metaSubtype === 'LOOKALIKE' ? 'Already a lookalike — pick a Custom audience as the seed' : 'Build a lookalike from this audience'}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-primary-soft hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Build lookalike"
                    >
                      <Wand2 size={14} />
                    </button>
                    <button
                      onClick={() => setDel(a)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-danger-soft hover:text-danger transition-colors"
                      aria-label="Delete audience"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Lookalike from Won leads modal */}
      <Modal
        open={lookalikeOpen}
        onClose={() => !building && setLookalikeOpen(false)}
        title="Build lookalike from Won leads"
        widthClass="max-w-md"
      >
        <div className="space-y-5">
          <p className="text-sm text-text-secondary">
            We'll hash and upload your won-lead phone numbers to Meta, then build a "similar to these people" audience automatically.
          </p>

          {/* How far back */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">How far back?</div>
            <div className="flex gap-2">
              {DAYS_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={clsx(
                    'flex-1 rounded-lg border py-2 text-sm font-medium transition-colors',
                    lookalikeState.sinceDays === d
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-border bg-surface text-text-secondary hover:border-primary hover:text-primary',
                  )}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          {/* Country */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">Country</div>
            <Select
              value={lookalikeState.country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>{c.label} ({c.code})</option>
              ))}
            </Select>
          </div>

          {/* Ratio */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">Audience size</div>
            <div className="flex gap-2">
              {RATIO_OPTIONS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setRatio(r.value)}
                  className={clsx(
                    'flex-1 rounded-lg border py-2 text-sm font-medium transition-colors',
                    lookalikeState.ratio === r.value
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-border bg-surface text-text-secondary hover:border-primary hover:text-primary',
                  )}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-text-muted">
              Percentage of {lookalikeState.country}'s Facebook population to target.
            </p>
          </div>

          {/* Name */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">Audience name</div>
            <Input
              value={lookalikeState.name}
              onChange={(e) => setLookalikeState((s) => ({ ...s, name: e.target.value }))}
              placeholder="e.g. Won leads (last 90d) — Lookalike 1% AE"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              variant="secondary"
              size="md"
              className="flex-1"
              onClick={() => setLookalikeOpen(false)}
              disabled={building}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              className="flex-1"
              loading={building}
              iconLeft={<Sparkles size={14} />}
              onClick={handleBuildLookalike}
            >
              Build audience
            </Button>
          </div>
        </div>
      </Modal>

      {/* Generic lookalike modal — seed from any existing custom audience */}
      <Modal
        open={genericOpen}
        onClose={() => !genericBuilding && setGenericOpen(false)}
        title="Build a lookalike audience"
        widthClass="max-w-md"
      >
        <div className="space-y-5">
          <p className="text-sm text-text-secondary">
            Lookalikes find new users on Meta who resemble an existing custom audience.
            Building takes 6–24 hours on Meta's side after creation.
          </p>

          {/* Seed audience picker */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">Seed audience</div>
            <Select
              value={genericState.seedId}
              onChange={(e) => {
                const seed = (items ?? []).find((a) => a.metaAudienceId === e.target.value);
                const next: GenericLookalikeState = {
                  ...genericState,
                  seedId: e.target.value,
                  seedName: seed?.name ?? '',
                };
                setGenericState({ ...next, name: suggestedGenericName(next) });
              }}
            >
              <option value="">— Pick a custom audience —</option>
              {(items ?? [])
                .filter((a) => a.metaSubtype !== 'LOOKALIKE')
                .map((a) => (
                  <option key={a.metaAudienceId} value={a.metaAudienceId}>
                    {a.name}{a.approximateCount ? ` (~${a.approximateCount.toLocaleString()})` : ''}
                  </option>
                ))}
            </Select>
            {genericState.seedId && (
              <p className="mt-1 text-2xs text-text-muted">
                Seeding from <strong className="text-text-secondary">{genericState.seedName}</strong>
              </p>
            )}
          </div>

          {/* Country */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">Country</div>
            <Select
              value={genericState.country}
              onChange={(e) => {
                const next = { ...genericState, country: e.target.value };
                setGenericState({ ...next, name: suggestedGenericName(next) });
              }}
            >
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>{c.label} ({c.code})</option>
              ))}
            </Select>
          </div>

          {/* Ratio */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">Audience size</div>
            <div className="flex gap-2">
              {RATIO_OPTIONS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => {
                    const next = { ...genericState, ratio: r.value };
                    setGenericState({ ...next, name: suggestedGenericName(next) });
                  }}
                  className={clsx(
                    'flex-1 rounded-lg border py-2 text-sm font-medium transition-colors',
                    genericState.ratio === r.value
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-border bg-surface text-text-secondary hover:border-primary hover:text-primary',
                  )}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-2xs text-text-muted">
              1% = closest match · 10% = broadest reach. Percentage of {genericState.country}'s Facebook population.
            </p>
          </div>

          {/* Name */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">Audience name</div>
            <Input
              value={genericState.name}
              onChange={(e) => setGenericState((s) => ({ ...s, name: e.target.value }))}
              placeholder="e.g. Loyal customers — Lookalike 1% AE"
            />
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="secondary" onClick={() => setGenericOpen(false)} disabled={genericBuilding}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={buildGenericLookalike}
              loading={genericBuilding}
              disabled={genericBuilding || !genericState.seedId || !genericState.name.trim()}
              iconLeft={<Wand2 size={14} />}
            >
              Build lookalike
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!del}
        title="Delete audience?"
        message={`Delete "${del?.name}"? This removes it from Meta and from Slightor. Ad Sets currently using this audience will continue to deliver against the cached audience until next refresh.`}
        danger
        onConfirm={async () => {
          await facebookAudiencesService.remove(del!.audienceUid);
          toast.success('Deleted');
          setDel(null);
          load(true);
        }}
        onCancel={() => setDel(null)}
      />
    </>
  );
}
